import { t } from "@hakili/i18n";
import { Button, ERROR_TEXT, Modal } from "@hakili/ui";
import type { ReactElement } from "react";

/**
 * `A3`, DEALER-CONFIRMS (architect's ruling of 30 October 2026): **the one confirmation the dealer's irreversible acts share.**
 *
 * « Retirer », « M'aligner », « Proposer » and « Maintenir » each say what will happen, in words the record gives, in the
 * kit's Modal, and write nothing until « confirmer ». « Annuler » is `common.action.annuler`. The act's own label is the
 * confirming button's, so the button that writes says the same thing as the one that asked.
 */
export function ConfirmDialog(props: {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly busy?: boolean;
  /** D2 `16-car-retirer`: the confirming button of an act that takes something away is drawn in the danger colour. */
  readonly danger?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  return (
    <Modal
      open
      title={<span data-testid="dealer-confirmer-titre">{props.title}</span>}
      onClose={props.onCancel}
      closeLabel={t("common.action.annuler")}
    >
      <div data-testid="dealer-confirmer">
        <p className="text-sm leading-relaxed text-gray-700" data-testid="dealer-confirmer-corps">
          {props.body}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={props.onCancel} data-testid="dealer-confirmer-annuler">
            {t("common.action.annuler")}
          </Button>
          {props.danger === true ? (
            // The kit's `danger` Button wears the palette's rose-700 (#BE123C): the page has ONE danger and ONE red, the kit's ERROR_TEXT (#A82019), and a named palette class beside an arbitrary one has no winner in the built CSS (the browser
            // frames spec caught it). So the danger button is drawn here with the kit's token and the kit's own shape, quiet edge and wash.
            <button
              type="button"
              disabled={props.busy === true}
              onClick={props.onConfirm}
              data-kit="button"
              data-variant="danger"
              data-size="md"
              className={`rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-medium hover:bg-rose-50 ${ERROR_TEXT} ${
                props.busy === true ? "cursor-not-allowed opacity-50" : ""
              }`}
              data-testid="dealer-confirmer-valider"
            >
              {props.confirmLabel}
            </button>
          ) : (
            <Button
              variant="primary"
              disabled={props.busy === true}
              onClick={props.onConfirm}
              data-testid="dealer-confirmer-valider"
            >
              {props.confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
